import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { sendRequest } from '../../utils/dateHelpers';
import { sleep } from '../../utils';
import { Skeleton } from '../ui';
import {
  PASSWORD_ERROR_MESSAGE,
  PASSWORD_REQUIREMENTS,
  getPasswordStrength,
  getPasswordStrengthText,
  validatePassword
} from '../../utils/passwordValidation';

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { userInfo } = useSelector(state => state);
  const requiresCurrentPassword =
    userInfo?.user?.requiresCurrentPassword !== false;

  const handleInputChange = e => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (requiresCurrentPassword && !formData.oldPassword) {
      newErrors.oldPassword = 'Current password is required';
    }

    if (!formData.password) {
      newErrors.password = 'New password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = PASSWORD_ERROR_MESSAGE;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (
      requiresCurrentPassword &&
      formData.oldPassword &&
      formData.password &&
      formData.oldPassword === formData.password
    ) {
      newErrors.password = 'New password must be different from old password';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleClick = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const response = await sendRequest('/settings/change-pwd', 'POST', {
        ...(requiresCurrentPassword
          ? { oldPassword: formData.oldPassword }
          : {}),
        password: formData.password
      });

      if (response.success) {
        setFormData({
          oldPassword: '',
          password: '',
          confirmPassword: ''
        });
        setErrors({});

        toast.success('Password successfully changed');
        await sleep(1000);
        window.location.href = '/settings/change-password';
      }
    } catch (error) {
      toast.error(error?.message || error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordStrengthInfo = getPasswordStrengthText(passwordStrength);

  return (
    <div className="mx-auto max-w-270">
      <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
        <div className="col-span-1 xl:col-span-3">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="p-6">
              <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Name
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {userInfo.user.fullName}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Email address
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {userInfo.user.email}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {requiresCurrentPassword ? (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Current Password *
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <LockIcon className="h-5 w-5 text-slate-400" />
                      </div>
                      <input
                        type={showOldPassword ? 'text' : 'password'}
                        name="oldPassword"
                        value={formData.oldPassword}
                        onChange={handleInputChange}
                        placeholder="Enter your current password"
                        className={`w-full rounded-xl border bg-white py-3 pl-10 pr-12 text-slate-900 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-900 dark:text-slate-100 ${
                          errors.oldPassword
                            ? 'border-red-500'
                            : 'border-slate-300 dark:border-slate-600'
                        }`}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                        disabled={loading}
                      >
                        {showOldPassword ? (
                          <VisibilityOffIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                        ) : (
                          <VisibilityIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                        )}
                      </button>
                    </div>
                    {errors.oldPassword && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.oldPassword}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900/70 dark:bg-blue-950/40 dark:text-blue-100">
                    This account was created with Google or email verification,
                    so you can set a password without entering a current one.
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    New Password *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <LockIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a strong password"
                      className={`w-full rounded-xl border bg-white py-3 pl-10 pr-12 text-slate-900 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-900 dark:text-slate-100 ${
                        errors.password
                          ? 'border-red-500'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      disabled={loading}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                      ) : (
                        <VisibilityIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                      )}
                    </button>
                  </div>

                  {formData.password && (
                    <div className="mt-2">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs text-slate-600 dark:text-slate-400">
                          Password Strength
                        </span>
                        <span
                          className={`rounded px-2 py-1 text-xs font-medium text-white ${passwordStrengthInfo.color}`}
                        >
                          {passwordStrengthInfo.text}
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${passwordStrengthInfo.color}`}
                          style={{
                            width: `${(passwordStrength / 5) * 100}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <LockIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your new password"
                      className={`w-full rounded-xl border bg-white py-3 pl-10 pr-12 text-slate-900 transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-900 dark:text-slate-100 ${
                        errors.confirmPassword
                          ? 'border-red-500'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      disabled={loading}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                      ) : (
                        <VisibilityIcon className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
                  <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                    Password Requirements:
                  </h4>
                  <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
                    {PASSWORD_REQUIREMENTS.map(item => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={handleClick}
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:from-slate-400 disabled:to-slate-500 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <Skeleton className="h-3 w-20 rounded-sm bg-white/50" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Change Password
                      <ArrowForwardIcon className="h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
